import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Building2, Users, Activity, Settings, Plus, Search, TrendingUp } from 'lucide-react';
import SystemMetrics from './components/SystemMetrics';
import CompaniesOverview from './components/CompaniesOverview';
import CreateCompanyModal from './components/CreateCompanyModal';

import UserManagementPanel from './components/UserManagementPanel';
import Icon from '../../components/AppIcon';
import PlatformAdminTester from './components/PlatformAdminTester';


export default function SystemManagementConsole() {
  const { user, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [systemStats, setSystemStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Check if user is platform admin
  const isPlatformAdmin = userProfile?.role === 'platform_admin';

  useEffect(() => {
    if (isPlatformAdmin) {
      loadSystemData();
    }
  }, [isPlatformAdmin]);

  const loadSystemData = async () => {
    setLoading(true);
    try {
      // Load system statistics
      const { data: stats, error: statsError } = await supabase?.rpc('get_company_statistics');
      
      if (!statsError && stats?.[0]) {
        setSystemStats(stats?.[0]);
      }

      // Load companies with user counts
      const { data: companiesData, error: companiesError } = await supabase?.from('companies')?.select(`
          *,
          user_profiles!inner(count)
        `)?.order('created_at', { ascending: false });

      if (!companiesError) {
        setCompanies(companiesData || []);
      }

      // Load users across all companies
      const { data: usersData, error: usersError } = await supabase?.from('user_profiles')?.select(`
          *,
          companies(name, slug)
        `)?.neq('role', 'platform_admin')?.order('created_at', { ascending: false })?.limit(100);

      if (!usersError) {
        setUsers(usersData || []);
      }

    } catch (error) {
      console.error('Error loading system data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async (companyData) => {
    try {
      const { data, error } = await supabase?.from('companies')?.insert([{
          name: companyData?.name,
          slug: companyData?.slug,
          status: companyData?.status || 'trial'
        }])?.select()?.single();

      if (error) throw error;

      // If admin user data provided, create the admin user
      if (companyData?.adminUser) {
        const { data: authData, error: authError } = await supabase?.auth?.admin?.createUser({
          email: companyData?.adminUser?.email,
          password: companyData?.adminUser?.password,
          email_confirm: true,
          user_metadata: {
            full_name: companyData?.adminUser?.fullName,
            role: 'company_admin'
          }
        });

        if (!authError && authData?.user) {
          // Create user profile
          await supabase?.from('user_profiles')?.insert([{
              id: authData?.user?.id,
              email: companyData?.adminUser?.email,
              full_name: companyData?.adminUser?.fullName,
              role: 'company_admin',
              company_id: data?.id,
              is_active: true
            }]);
        }
      }

      setShowCreateCompany(false);
      await loadSystemData();
      
    } catch (error) {
      console.error('Error creating company:', error);
    }
  };

  // Redirect non-platform admins
  if (!isPlatformAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-red-100 rounded-full p-3 mx-auto w-16 h-16 flex items-center justify-center mb-4">
            <Settings className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You need platform administrator privileges to access the System Management Console.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading system data...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'System Overview', icon: TrendingUp },
    { id: 'companies', label: 'Companies', icon: Building2 },
    { id: 'users', label: 'User Management', icon: Users },
    { id: 'activity', label: 'System Activity', icon: Activity },
    { id: 'settings', label: 'Platform Settings', icon: Settings }
  ];

  const filteredCompanies = companies?.filter(company =>
    company?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    company?.slug?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  ) || [];

  const filteredUsers = users?.filter(user =>
    user?.full_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    user?.companies?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  ) || [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">System Management Console</h1>
              <p className="mt-1 text-sm text-gray-600">
                Platform administration and system monitoring
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span>System Operational</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Navigation Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {tabs?.map((tab) => {
              const Icon = tab?.icon;
              return (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab?.id
                      ? 'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab?.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <SystemMetrics stats={systemStats} companies={companies} />
        )}

        {activeTab === 'companies' && (
          <div className="space-y-6">
            {/* Companies Header */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Companies Management</h2>
                <p className="text-gray-600">Manage all companies in the platform</p>
              </div>
              <button
                onClick={() => setShowCreateCompany(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>Create Company</span>
              </button>
            </div>

            {/* Search and Filters */}
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search companies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <CompaniesOverview 
              companies={filteredCompanies} 
              onRefresh={loadSystemData}
            />
          </div>
        )}

        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
                <p className="text-gray-600">Manage users across all companies</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <UserManagementPanel 
              users={filteredUsers} 
              companies={companies}
              onRefresh={loadSystemData}
            />
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">System Activity</h2>
            <div className="text-center py-12 text-gray-500">
              <Activity className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Activity monitoring will be available in the next update</p>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Platform Settings</h2>
            <div className="text-center py-12 text-gray-500">
              <Settings className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Platform configuration settings will be available soon</p>
            </div>
          </div>
        )}
        
        <div className="mb-8">
          <PlatformAdminTester />
        </div>
      </main>
      {/* Create Company Modal */}
      {showCreateCompany && (
        <CreateCompanyModal
          onClose={() => setShowCreateCompany(false)}
          onCreate={handleCreateCompany}
        />
      )}
    </div>
  );
}